// PPF wrap (CORE "W" tool): extend chosen edges of a pattern outward by a wrap depth so the film tucks around panel edges.
// Edges are the runs between true corners; a wrap is job-only (the library template is untouched).
import { bbox, cornerFlags, signedArea } from "./geometry";

export const WRAP_DEPTHS = [[0.25, '1/4"'], [0.375, '3/8"'], [0.5, '1/2"']];
export const WRAP_DEFAULT = 0.5;
const MITRE_MIN = 0.35;

const unit = ([x, y]) => { const l = Math.hypot(x, y) || 1; return [x / l, y / l]; };
// outward normal of segment i (pts[i] → pts[i+1])
const normals = (pts) => {
  const n = pts.length, sign = signedArea(pts) > 0 ? 1 : -1;
  return pts.map((a, i) => { const b = pts[(i + 1) % n]; const e = [b[0] - a[0], b[1] - a[1]]; return unit(sign > 0 ? [e[1], -e[0]] : [-e[1], e[0]]); });
};
const mitre = (v, n1, n2, d) => {
  let bis = [n1[0] + n2[0], n1[1] + n2[1]];
  bis = Math.hypot(bis[0], bis[1]) < 1e-6 ? n1 : unit(bis);
  const k = d / Math.max(MITRE_MIN, bis[0] * n1[0] + bis[1] * n1[1]);
  return [v[0] + bis[0] * k, v[1] + bis[1] * k];
};

// Edge groups: {segs: [segment indices], pts: polyline} — one per run between corners (an outline without corners is split in four).
export function edgeGroups(pts) {
  const n = pts.length;
  if (n < 3) return [];
  const flags = cornerFlags(pts);
  let corners = flags.map((f, i) => (f ? i : -1)).filter((i) => i >= 0);
  if (corners.length < 2) corners = [0, 1, 2, 3].map((k) => Math.floor((n * k) / 4));
  return corners.map((c, k) => {
    const end = corners[(k + 1) % corners.length];
    const segs = [];
    for (let i = c; i !== end; i = (i + 1) % n) segs.push(i);
    const line = segs.map((i) => pts[i]).concat([pts[end]]);
    return { segs, pts: line };
  });
}

// Outline with the selected segments pushed out by `depth` (square ends where a wrapped edge meets an unwrapped one).
export function applyWrap(base, segs, depth) {
  const sel = new Set(segs);
  if (!sel.size || !depth) return base;
  const n = base.length, N = normals(base), out = [];
  for (let i = 0; i < n; i++) {
    const v = base[i], prev = sel.has((i - 1 + n) % n), next = sel.has(i);
    if (prev && next) out.push(mitre(v, N[(i - 1 + n) % n], N[i], depth));
    else if (prev) { const m = N[(i - 1 + n) % n]; out.push([v[0] + m[0] * depth, v[1] + m[1] * depth], v); }
    else if (next) { const m = N[i]; out.push(v, [v[0] + m[0] * depth, v[1] + m[1] * depth]); }
    else out.push(v);
  }
  return out;
}

// The added film as polygons (one per contiguous run of wrapped segments) — drawn light blue on the canvas.
export function wrapBands(base, segs, depth) {
  const sel = new Set(segs);
  if (!sel.size || !depth) return [];
  const n = base.length, N = normals(base);
  const runs = [];
  let start = [...sel].find((i) => !sel.has((i - 1 + n) % n));
  if (start === undefined) start = 0;  // every edge wrapped
  const seen = new Set();
  for (let s = start, guard = 0; guard < n && !seen.has(s); guard++) {
    if (!sel.has(s)) { s = (s + 1) % n; continue; }
    const run = [];
    for (let i = s; sel.has(i) && !seen.has(i); i = (i + 1) % n) { run.push(i); seen.add(i); }
    runs.push(run);
    s = (run[run.length - 1] + 1) % n;
  }
  return runs.map((run) => {
    const inner = run.map((i) => base[i]).concat([base[(run[run.length - 1] + 1) % n]]);
    const outer = inner.map((v, k) => {
      if (k === 0) { const m = N[run[0]]; return [v[0] + m[0] * depth, v[1] + m[1] * depth]; }
      if (k === inner.length - 1) { const m = N[run[run.length - 1]]; return [v[0] + m[0] * depth, v[1] + m[1] * depth]; }
      return mitre(v, N[run[k - 1]], N[run[k]], depth);
    });
    return inner.concat(outer.reverse());
  });
}

// Mirror a wrap onto the opposite-side piece: each wrapped edge is matched by its mirrored midpoint to the mate's closest edge group.
export function mirrorWrapSegs(srcBase, srcSegs, dstBase) {
  const sb = bbox(srcBase), db = bbox(dstBase);
  const rel = (p, b, flip) => [((flip ? b.maxX - p[0] : p[0] - b.minX) / (b.w || 1)), (p[1] - b.minY) / (b.h || 1)];
  const groups = edgeGroups(dstBase);
  const mids = groups.map((g) => { const m = g.pts.reduce((s, p) => [s[0] + p[0], s[1] + p[1]], [0, 0]); return rel([m[0] / g.pts.length, m[1] / g.pts.length], db, false); });
  const out = new Set();
  for (const g of edgeGroups(srcBase)) {
    if (!g.segs.every((i) => srcSegs.includes(i))) continue;
    const m = g.pts.reduce((s, p) => [s[0] + p[0], s[1] + p[1]], [0, 0]);
    const q = rel([m[0] / g.pts.length, m[1] / g.pts.length], sb, true);
    let best = 0, bd = Infinity;
    mids.forEach((c, k) => { const d = Math.hypot(c[0] - q[0], c[1] - q[1]); if (d < bd) { bd = d; best = k; } });
    groups[best].segs.forEach((i) => out.add(i));
  }
  return [...out];
}

// Piece after its wrap settings changed: points rebuilt from the untouched base outline.
export function wrapPiece(p, segs, depth) {
  const base = p.wrapBase || p.points;
  if (!segs.length) return { ...p, points: base, wrap: null, wrapBase: null, edited: Boolean(p.wrapEdited) };
  return { ...p, points: applyWrap(base, segs, depth), wrap: { segs, depth }, wrapBase: base, wrapEdited: p.wrap ? p.wrapEdited : p.edited, edited: true };
}
